/**
 * PORT-008 §8.2 — l'espace prend les couleurs de l'organisme, jamais d'Argalis.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DEUX SOURCES, UNE APPLICATION
 * ═══════════════════════════════════════════════════════════════════════════
 * · **Avant connexion** : `GET /api/portal/branding` — l'entité RACINE, seule
 *   que le sous-domaine désigne.
 * · **Après** : `organisation` de `/api/portal/me` — l'entité du contexte
 *   ouvert, qui peut être une agence avec ses propres couleurs. Le shell la
 *   préfère dès qu'il l'a, et le thème suit chaque changement de contexte.
 *
 * ⚠️ Une couleur absente ou invalide donne les tokens de la PLATEFORME. Ce sont
 * des **couleurs** de repli, jamais un **logo** de repli : afficher le mauvais
 * logo sur une page qui demande un mot de passe est exactement ce qu'on apprend
 * aux gens à fuir.
 *
 * ⚠️ Les variables vivent sur `document.documentElement`, pas sur un conteneur :
 * `DropdownMenu`, `Sheet` et `Popover` rendent dans `body` par portail, hors de
 * tout conteneur teinté. Elles sont **retirées au démontage** — sans quoi la
 * couleur d'un organisme survivrait à la navigation et teindrait la page
 * suivante.
 *
 * ⭐ **Et ce que lit le SYSTÈME, pas seulement React** — `useManifesteDeMarque`.
 * Ce composant est déjà « le SEUL écrivain de `document.title` du dépôt » ; le
 * manifeste PWA, les balises `apple-*` et `theme-color` relèvent de la même
 * responsabilité, et ils étaient restés sur « Argalis » : un apprenant qui
 * posait son espace sur son écran d'accueil obtenait une icône Argalis qui
 * ouvrait le login du back-office. Voir l'en-tête de ce module.
 *
 * ⚠️ Le manifeste suit `GET /api/portal/branding` — l'entité RACINE du
 * sous-domaine — et non l'`organisation` passée en props, qui peut être une
 * agence du contexte ouvert. Ce n'est pas une inconséquence : un manifeste est
 * un objet d'ORIGINE, une seule icône par sous-domaine, et changer de contexte
 * ne doit pas renommer une application déjà installée.
 */

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useBrandThemeOnRoot } from '@/lib/brandTheme'
import { usePortalBranding } from '@/hooks/usePortalMe'

import { useManifesteDeMarque } from './useManifesteDeMarque'

interface PortalBrandThemeProps {
    name: string | null | undefined
    primaryColor: string | null | undefined
    secondaryColor?: string | null
    faviconUrl?: string | null
}

/** Une icône neutre — jamais celle d'Argalis devant une personne externe. */
const FAVICON_NEUTRE =
    'data:image/svg+xml,' +
    encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">' +
        '<rect width="32" height="32" rx="7" fill="%23e5e7eb"/></svg>',
    )

export function PortalBrandTheme({
    name,
    primaryColor,
    secondaryColor,
    faviconUrl,
}: PortalBrandThemeProps) {
    // `important: true` : `html.neutral` déclare `--primary` & co en
    // `!important`, et une inline ordinaire y perdrait (§8.2).
    useBrandThemeOnRoot(primaryColor, secondaryColor, { important: true })

    // Requête PARTAGÉE : même clé react-query que celle de `PortalAuthLayout`,
    // donc aucun appel supplémentaire sur les pages d'accès, et un seul appel
    // public sur la coque.
    const { data: habillagePublic } = usePortalBranding()
    useManifesteDeMarque(habillagePublic)
    const { t } = useTranslation()

    /**
     * UX-14 — le titre de l'onglet, dans la langue de l'INTERFACE.
     *
     * Il était écrit `Espace ${name}` en dur — donc en français dans l'interface
     * anglaise — et, nom absent, l'effet sortait sans rien écrire : l'onglet
     * gardait le titre d'index, « Argalis V2 ». ⛔ Jamais ce titre-là : sans nom,
     * « Mon espace », qui est vrai pour les trois publics.
     */
    useEffect(() => {
        const avant = document.title
        const nom = name?.trim()
        document.title = nom
            ? t('portal.brand.documentTitle', 'Espace {{name}}', { name: nom })
            : t('portal.context.kind.mine')
        return () => { document.title = avant }
    }, [name, t])

    useEffect(() => {
        const lien = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
        if (!lien) return
        const avant = lien.href
        lien.href = faviconUrl || FAVICON_NEUTRE
        return () => { lien.href = avant }
    }, [faviconUrl])

    return null
}
